import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'

const LeftToolbarContext = createContext(null)

/** El contenedor donde se dibujan los tooltips y los menús de la barra, o null fuera de ella. */
export const useLeftToolbar = () => useContext(LeftToolbarContext)

// Lo que se difumina arriba y abajo cuando hay botones fuera de la vista.
const FADE_PX = 22

/**
 * Barra de herramientas de la izquierda del editor.
 *
 * Nunca es más alta que la pantalla: si no caben todos los botones, la zona de
 * botones se desplaza en vertical y se difumina por el lado donde hay más.
 *
 * Un contenedor con scroll recorta también en horizontal, y los tooltips y los
 * menús de esta barra se abren hacia la derecha, fuera de ella. Por eso viven
 * en una capa aparte (`layer`), hermana de la zona con scroll y dentro de la
 * barra, que no recorta. Los menús pasan por `SideMenu`; los tooltips son los
 * `data-tip` de siempre, que aquí se dibujan a mano (los de CSS se ocultan).
 */
const LeftToolbar = ({ children }) => {
    const rootRef = useRef(null)
    const scrollerRef = useRef(null)
    const [layer, setLayer] = useState(null)
    // Estable: una función nueva en cada render haría que React la volviera a llamar.
    const setRoot = useCallback((node) => {
        rootRef.current = node
        setLayer(node)
    }, [])
    const [fades, setFades] = useState({ top: 0, bottom: 0 })
    const [tip, setTip] = useState(null)

    const updateFades = useCallback(() => {
        const scroller = scrollerRef.current
        if (!scroller) return
        const hidden = scroller.scrollHeight - scroller.clientHeight
        if (hidden <= 1) {
            setFades((current) => (current.top || current.bottom ? { top: 0, bottom: 0 } : current))
            return
        }
        const next = {
            top: scroller.scrollTop > 1 ? FADE_PX : 0,
            bottom: scroller.scrollTop < hidden - 1 ? FADE_PX : 0,
        }
        setFades((current) => (current.top === next.top && current.bottom === next.bottom ? current : next))
    }, [])

    // La altura disponible o los botones cambian (selección, ventana, modo 3D).
    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller) return undefined
        updateFades()
        const observer = new ResizeObserver(updateFades)
        observer.observe(scroller)
        Array.from(scroller.children).forEach((child) => observer.observe(child))
        const mutations = new MutationObserver(() => {
            Array.from(scroller.children).forEach((child) => observer.observe(child))
            updateFades()
        })
        mutations.observe(scroller, { childList: true })
        return () => {
            observer.disconnect()
            mutations.disconnect()
        }
    }, [updateFades])

    // Cuánto se desplaza cada pulsación de las flechas: casi una pantalla de botones.
    const scrollByPage = (direction) => {
        const scroller = scrollerRef.current
        scroller?.scrollBy({ top: direction * scroller.clientHeight * 0.7, behavior: 'smooth' })
    }

    // Las flechas no son hijas de la zona con scroll, así que un dedo que empieza
    // a deslizar justo encima no la desplazaría: se lo pasan a mano. Un arrastre
    // no cuenta como pulsación (si no, soltar encima desplazaría una página más).
    const drag = useRef(null)
    const dragHandlers = (direction) => ({
        onPointerDown: (event) => {
            if (event.pointerType !== 'touch') return
            drag.current = { startY: event.clientY, startScroll: scrollerRef.current.scrollTop, moved: false }
            event.currentTarget.setPointerCapture?.(event.pointerId)
        },
        onPointerMove: (event) => {
            const state = drag.current
            if (!state) return
            const delta = event.clientY - state.startY
            if (!state.moved && Math.abs(delta) < 6) return
            state.moved = true
            scrollerRef.current.scrollTop = state.startScroll - delta
        },
        onPointerUp: () => { setTimeout(() => { drag.current = null }, 0) },
        onPointerCancel: () => { drag.current = null },
        onClick: () => { if (!drag.current?.moved) scrollByPage(direction) },
    })

    const showTip = (target) => {
        const text = target?.getAttribute('data-tip')
        const root = rootRef.current
        if (!text || !root) return
        const box = target.getBoundingClientRect()
        const rootBox = root.getBoundingClientRect()
        setTip({ text, top: box.top - rootBox.top + box.height / 2 })
    }

    // Con ratón o con el teclado, no con el dedo: en táctil no hay "pasar por encima".
    const handlePointerOver = (event) => {
        if (event.pointerType === 'touch') return
        showTip(event.target.closest?.('[data-tip]'))
    }
    const handleFocus = (event) => {
        if (event.target.matches?.(':focus-visible')) showTip(event.target.closest?.('[data-tip]'))
    }
    const hideTip = () => setTip(null)

    return (
        <LeftToolbarContext.Provider value={layer}>
            <div
                ref={setRoot}
                className="editortabs-container customiz3r-leftbar p-3 px-2 flex flex-col items-center max-h-full"
            >
                <div
                    ref={scrollerRef}
                    className="customiz3r-leftbar__scroller"
                    style={{ '--fade-top': `${fades.top}px`, '--fade-bottom': `${fades.bottom}px` }}
                    onScroll={() => { updateFades(); hideTip() }}
                    onPointerOver={handlePointerOver}
                    onPointerOut={hideTip}
                    onFocus={handleFocus}
                    onBlur={hideTip}
                    onPointerDown={hideTip}
                >
                    {children}
                </div>

                {/* Marcadores de que hay más botones: más claros que el degradado, y
                    se pueden pulsar. Solo existen por el lado donde queda algo. */}
                {fades.top > 0 && (
                    <button
                        type="button"
                        className="customiz3r-leftbar__more customiz3r-leftbar__more--up"
                        aria-label="Ver las herramientas de arriba"
                        title="Más herramientas arriba"
                        {...dragHandlers(-1)}
                    >
                        <ChevronUp className="h-5 w-5" />
                    </button>
                )}
                {fades.bottom > 0 && (
                    <button
                        type="button"
                        className="customiz3r-leftbar__more customiz3r-leftbar__more--down"
                        aria-label="Ver las herramientas de abajo"
                        title="Más herramientas abajo"
                        {...dragHandlers(1)}
                    >
                        <ChevronDown className="h-5 w-5" />
                    </button>
                )}

                {layer && tip && createPortal(
                    <div className="customiz3r-leftbar__tip" style={{ top: tip.top }} role="tooltip">
                        {tip.text}
                    </div>,
                    layer,
                )}
            </div>
        </LeftToolbarContext.Provider>
    )
}

LeftToolbar.propTypes = {
    children: PropTypes.node,
}

export default LeftToolbar
